import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete, ApiError } from '../lib/api';
import { toast } from '../lib/toast';

// NOTE: client mirrors the server render function. Keeping these in sync is
// the trade-off for live preview without a server round-trip on every keystroke.
// If you change the render logic in src/lib/confirmation_email.ts, mirror it here.
const DROPPABLE = ['pet', 'price', 'location', 'phone'] as const;
function renderPreview(template: { subject: string; body: string }, ctx: Record<string, string>): { subject: string; body: string } {
  function substitute(text: string): string {
    return text
      .replace(/\{customer\}/g, ctx.customer || 'there')
      .replace(/\{operator\}/g, ctx.operator || ctx.tenant)
      .replace(/\{date\}/g, ctx.date)
      .replace(/\{time\}/g, ctx.time)
      .replace(/\{service\}/g, ctx.service)
      .replace(/\{tenant\}/g, ctx.tenant)
      .replace(/\{pet\}/g, ctx.pet || '')
      .replace(/\{price\}/g, ctx.price || '')
      .replace(/\{location\}/g, ctx.location || '')
      .replace(/\{phone\}/g, ctx.phone || '');
  }
  function dropPhrases(line: string): string {
    for (const tk of DROPPABLE) {
      if (ctx[tk]) continue;
      const patterns = [
        new RegExp(`\\s+for\\s+\\{${tk}\\}`, 'g'),
        new RegExp(`\\s+with\\s+\\{${tk}\\}`, 'g'),
        new RegExp(`\\s+at\\s+\\{${tk}\\}`, 'g'),
        new RegExp(`\\s+·\\s+\\{${tk}\\}`, 'g'),
        new RegExp(`\\s+-\\s+\\{${tk}\\}`, 'g'),
        new RegExp(`\\{${tk}\\}\\s+·\\s+`, 'g'),
        new RegExp(`^[\\w][\\w\\s]*:\\s*\\{${tk}\\}`, 'g'),
        new RegExp(`\\{${tk}\\}`, 'g'),
      ];
      for (const re of patterns) line = line.replace(re, '');
    }
    return line;
  }
  const lines = template.body.split('\n');
  const out: string[] = [];
  for (const raw of lines) {
    let line = dropPhrases(raw);
    line = substitute(line);
    if (line.replace(/[\s·\-—,.]/g, '').length === 0 && raw.trim() !== '') continue;
    out.push(line);
  }
  return { subject: substitute(template.subject).trim(), body: out.join('\n').replace(/\n{3,}/g, '\n\n') };
}

const PLACEHOLDERS = ['customer', 'pet', 'date', 'time', 'service', 'price', 'location', 'phone', 'operator', 'tenant'] as const;

interface TemplateEditorProps {
  variant: 'confirmation' | 'update';
  label: string;
  description: string;
  sampleCtx: Record<string, string>;
}

function TemplateEditor({ variant, label, description, sampleCtx }: TemplateEditorProps) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    apiGet<{ subject: string; body: string; is_default: boolean }>(`/api/tenants/me/email-templates/${variant}`)
      .then((r) => { setSubject(r.subject); setBody(r.body); setIsDefault(r.is_default); setLoaded(true); })
      .catch((e) => toast.error('Load failed: ' + (e as Error).message));
  }, [variant]);

  function insertPlaceholder(token: string) {
    const ta = bodyRef.current;
    const insert = `{${token}}`;
    if (!ta) { setBody(body + insert); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = body.slice(0, start) + insert + body.slice(end);
    setBody(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + insert.length, start + insert.length);
    }, 0);
  }

  async function save() {
    setBusy(true);
    try {
      const r = await apiPut<{ subject: string; body: string; is_default: boolean }>(
        `/api/tenants/me/email-templates/${variant}`, { subject, body }
      );
      setIsDefault(r.is_default);
      toast.success('Saved');
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        toast.error('Only the owner can edit email templates');
      } else {
        toast.error((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    try {
      const r = await apiDelete<{ subject: string; body: string; is_default: boolean }>(
        `/api/tenants/me/email-templates/${variant}`
      );
      setSubject(r.subject); setBody(r.body); setIsDefault(true);
      toast.success('Reset to default');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    try {
      const r = await apiPost<{ sent: boolean; to: string }>(
        `/api/tenants/me/email-templates/${variant}/test`, { subject, body }
      );
      toast.success(`Test email sent to ${r.to}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        toast.error('Too many test sends — wait a minute');
      } else {
        toast.error((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) {
    return <div className="card text-sm text-muted">Loading {label}…</div>;
  }

  const preview = renderPreview({ subject, body }, sampleCtx);

  return (
    <div className="card space-y-4">
      <div>
        <h2 className="font-semibold">{label}</h2>
        <p className="text-sm text-muted">{description}</p>
        {isDefault && (
          <p className="text-xs text-amber-300 mt-1">Using the default template. Edits will customize it for your business.</p>
        )}
      </div>

      <div>
        <label className="label">Subject</label>
        <input className="input w-full" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
      </div>

      <div>
        <label className="label">Body</label>
        <textarea ref={bodyRef} className="input w-full min-h-[180px] font-mono text-sm" value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} />
      </div>

      <div>
        <div className="text-xs text-muted mb-1">Click to insert:</div>
        <div className="flex flex-wrap gap-1.5">
          {PLACEHOLDERS.map((p) => (
            <button key={p} type="button" onClick={() => insertPlaceholder(p)} className="text-xs px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-text/80 font-mono">
              {`{${p}}`}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-white/5 pt-3">
        <div className="text-xs text-muted mb-1">Preview</div>
        <div className="rounded-md bg-black/30 p-3 text-sm">
          <div className="text-muted text-xs">Subject:</div>
          <div className="mb-2">{preview.subject}</div>
          <div className="text-muted text-xs">Body:</div>
          <div className="whitespace-pre-wrap">{preview.body}</div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={reset} disabled={busy} className="btn-ghost px-3 py-2">Reset to default</button>
        <button type="button" onClick={sendTest} disabled={busy} className="btn-ghost px-3 py-2">Send test email to me</button>
        <button type="button" onClick={save} disabled={busy} className="btn-primary px-3 py-2">{busy ? 'Saving…' : 'Save changes'}</button>
      </div>
    </div>
  );
}

export function SettingsEmails() {
  const [tenantSample, setTenantSample] = useState<Record<string, string>>({
    customer: 'Sarah',
    pet: 'Bruno',
    date: 'Sat May 17',
    time: '10:00 AM',
    service: 'Bath & Brush',
    price: '$45.00',
    location: '',
    phone: '',
    operator: 'Rae',
    tenant: 'Your Business',
  });

  useEffect(() => {
    apiGet<{ tenant: { name: string; settings: Record<string, unknown> } }>('/api/tenants/me')
      .then((r) => {
        setTenantSample((prev) => ({
          ...prev,
          tenant: r.tenant.name,
          location: typeof r.tenant.settings.address === 'string' ? r.tenant.settings.address as string : '',
          phone: typeof r.tenant.settings.phone === 'string' ? r.tenant.settings.phone as string : '',
        }));
      })
      .catch(() => { /* preview still works with defaults */ });
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Email templates</h1>
        <Link to="/settings" className="text-sm text-muted inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Settings
        </Link>
      </div>

      <TemplateEditor
        variant="confirmation"
        label="Appointment confirmation"
        description="Sent to the customer when you mark an appointment as confirmed."
        sampleCtx={tenantSample}
      />

      <TemplateEditor
        variant="update"
        label="Appointment update"
        description="Sent when a confirmed appointment is rescheduled or the service changes."
        sampleCtx={tenantSample}
      />
    </div>
  );
}
